(() => {
  const root = window.Shopify?.routes?.root || '/';
  const fallbackHandles = [
    'the-ribbed-polo-chocolate',
    'the-tailored-trouser-sand',
    'the-classic-loafer-chocolate',
    'the-cable-quarter-zip-ivory',
    'the-suede-belt-chocolate',
    'the-low-profile-trainer-taupe',
    'the-essential-polo-sand',
    'the-pocket-overshirt-light-beige',
    'the-low-profile-trainer-black',
    'the-smooth-belt-black',
    'the-long-sleeve-shirt-white'
  ];
  const cache = new Map();
  let running = false;
  let queued = false;
  let timer;

  const getJson = async path => {
    const response = await fetch(root + path, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Request failed: ' + response.status);
    return response.json();
  };
  const cached = path => {
    if (!cache.has(path)) cache.set(path, getJson(path).catch(() => null));
    return cache.get(path);
  };
  const numericId = id => String(id || '').split('/').pop();
  const category = item => {
    const value = [item.title, item.product_type, item.type].filter(Boolean).join(' ').toLowerCase();
    if (/trouser|jean|chino|pant|bottom/.test(value)) return 'bottom';
    if (/loafer|trainer|mule|shoe|footwear/.test(value)) return 'footwear';
    if (/belt|cap|beanie|accessor/.test(value)) return 'accessory';
    if (/jacket|coat|overshirt|puffer/.test(value)) return 'outerwear';
    if (/polo|knit|shirt|tee|sweater|crewneck|cardigan/.test(value)) return 'top';
    return 'other';
  };
  const available = product => Array.isArray(product?.variants) && product.variants.some(variant => variant.available);
  const price = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: currency || 'GBP'
      }).format(Number(cents) / 100);
    } catch {
      return '£' + (Number(cents) / 100).toFixed(2);
    }
  };

  async function candidates(cart) {
    const ids = [...new Set(cart.items.map(item => numericId(item.product_id)))].filter(Boolean).slice(0, 2);
    const pool = [];
    const requests = ['complementary', 'related'].flatMap(intent => ids.map(id => ({
      source: intent,
      path: 'recommendations/products.json?product_id=' + encodeURIComponent(id) + '&limit=10&intent=' + intent
    })));
    requests.push(...fallbackHandles.map(handle => ({ source: 'curated', path: 'products/' + handle + '.js' })));
    const results = await Promise.all(requests.map(request => cached(request.path)));
    results.forEach((result, index) => {
      if (requests[index].source === 'curated') {
        if (result) pool.push({ product: result, source: 'curated' });
      } else result?.products?.forEach(product => pool.push({ product, source: requests[index].source }));
    });
    const inCart = new Set(cart.items.map(item => String(item.handle)));
    const present = new Set(cart.items.map(category));
    const priorities = ['top', 'bottom', 'footwear', 'accessory', 'outerwear']
      .filter(type => !present.has(type));
    const seen = new Set();
    const ranked = pool.filter(({ product }) => {
      if (!product?.handle || seen.has(product.handle) || inCart.has(product.handle)
        || present.has(category(product)) || category(product) === 'other' || !available(product)) return false;
      seen.add(product.handle);
      return true;
    }).map(({ product, source }) => ({
      product,
      type: category(product),
      score: (priorities.includes(category(product)) ? 100 - priorities.indexOf(category(product)) * 15 : 0)
        + (source === 'complementary' ? 25 : source === 'curated' ? 15 : 0)
    })).sort((a, b) => b.score - a.score);
    const picks = [];
    const usedTypes = new Set();
    for (const entry of ranked) {
      if (usedTypes.has(entry.type)) continue;
      picks.push(entry.product);
      usedTypes.add(entry.type);
      if (picks.length === 8) break;
    }
    const typeCounts = new Map(picks.map(product => [category(product), 1]));
    for (const entry of ranked) {
      if (picks.length === 8) break;
      if (!picks.includes(entry.product) && (typeCounts.get(entry.type) || 0) < 3) {
        picks.push(entry.product);
        typeCounts.set(entry.type, (typeCounts.get(entry.type) || 0) + 1);
      }
    }
    return picks;
  }

  function makeCard(product, currency) {
    const card = document.createElement('article');
    card.className = 'hv-cart-rec__card';
    card.setAttribute('role', 'listitem');
    const link = document.createElement('a');
    link.className = 'hv-cart-rec__image-link';
    link.href = '/products/' + encodeURIComponent(product.handle);
    const image = document.createElement('img');
    image.className = 'hv-cart-rec__image';
    image.src = typeof product.featured_image === 'string'
      ? product.featured_image
      : product.featured_image?.src || product.images?.[0] || '';
    image.alt = product.title;
    image.loading = 'lazy';
    link.append(image);
    const name = document.createElement('a');
    name.className = 'hv-cart-rec__name';
    name.href = link.href;
    name.textContent = product.title;
    const amount = document.createElement('strong');
    amount.className = 'hv-cart-rec__price';
    amount.textContent = price(product.price, currency);
    card.append(link, name, amount);

    const variants = product.variants.filter(variant => variant.available);
    let selector;
    if (variants.length > 1) {
      selector = document.createElement('select');
      selector.className = 'hv-cart-rec__size';
      selector.setAttribute('aria-label', 'Choose size for ' + product.title);
      variants.forEach(variant => {
        const option = document.createElement('option');
        option.value = numericId(variant.id);
        option.textContent = variant.public_title || variant.title;
        selector.append(option);
      });
      card.append(selector);
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hv-cart-rec__add';
    button.textContent = 'Add to bag';
    const error = document.createElement('p');
    error.className = 'hv-cart-rec__error';
    error.setAttribute('role', 'status');
    button.addEventListener('click', async () => {
      button.disabled = true;
      button.textContent = 'Adding…';
      error.textContent = '';
      try {
        const variantId = selector?.value || numericId(variants[0].id);
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Could not add this item');
        const drawer = document.querySelector('cart-drawer');
        if (result.sections?.['cart-drawer'] && typeof drawer?.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
        }
        schedule();
      } catch (cause) {
        error.textContent = cause.message || 'Please try again';
      } finally {
        button.disabled = false;
        button.textContent = 'Add to bag';
      }
    });
    card.append(button, error);
    return card;
  }

  function rail(body) {
    let section = body.querySelector('.hv-cart-rec');
    if (section) return section;
    section = document.createElement('section');
    section.className = 'hv-cart-rec';
    section.setAttribute('aria-label', 'Complete your order');
    const heading = document.createElement('div');
    heading.className = 'hv-cart-rec__heading';
    const labels = document.createElement('div');
    const eyebrow = document.createElement('span');
    eyebrow.className = 'hv-cart-rec__eyebrow';
    eyebrow.textContent = 'THE FINISHING TOUCH';
    const title = document.createElement('h3');
    title.className = 'hv-cart-rec__title';
    title.textContent = 'Complete your order';
    labels.append(eyebrow, title);
    const next = document.createElement('button');
    next.className = 'hv-cart-rec__next';
    next.type = 'button';
    next.setAttribute('aria-label', 'Scroll recommendations');
    next.textContent = '›';
    heading.append(labels, next);
    const track = document.createElement('div');
    track.className = 'hv-cart-rec__track';
    track.setAttribute('role', 'list');
    next.addEventListener('click', () => track.scrollBy({ left: 172, behavior: 'smooth' }));
    section.append(heading, track);
    body.append(section);
    return section;
  }

  async function update() {
    if (running) { queued = true; return; }
    const drawer = document.querySelector('cart-drawer');
    if (!drawer?.classList.contains('active')) return;
    const body = drawer.querySelector('.cart-drawer__body');
    if (!body) return;
    running = true;
    try {
      const cart = await getJson('cart.js');
      if (!body.isConnected) return;
      if (!cart.item_count) { body.querySelector('.hv-cart-rec')?.remove(); return; }
      const signature = cart.items.map(item => item.key + ':' + item.quantity).sort().join('|');
      let section = body.querySelector('.hv-cart-rec');
      if (section?.dataset.cartSignature === signature) return;
      section = rail(body);
      const track = section.querySelector('.hv-cart-rec__track');
      if (!track.children.length) {
        track.replaceChildren(...Array.from({ length: 2 }, () => {
          const placeholder = document.createElement('div');
          placeholder.className = 'hv-cart-rec__placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          return placeholder;
        }));
      }
      const products = await candidates(cart);
      if (!body.isConnected) return;
      if (!products.length) { section?.remove(); return; }
      track.replaceChildren(...products.map(product => makeCard(product, cart.currency)));
      section.dataset.cartSignature = signature;
    } catch (error) {
      console.warn('HEIRVALE cart recommendations:', error);
    } finally {
      running = false;
      if (queued) { queued = false; schedule(); }
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(update, 120);
  }
  document.addEventListener('click', event => {
    if (event.target.closest('#cart-icon-bubble, cart-drawer')) schedule();
  }, true);
  const observer = new MutationObserver(records => {
    if (records.some(record => !record.target.closest?.('.hv-cart-rec'))) schedule();
  });
  const start = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
